import { forwardRef } from 'react'
import { cn } from './internal/utils'
import { PRESET_COLORS, isPresetColor } from './internal/presets'

const STATUS_DOT_COLOR = {
  success: 'var(--success-green)',
  processing: 'var(--info-blue)',
  default: 'var(--gray-500)',
  error: 'var(--error-color)',
  warning: 'var(--warning-color)',
}

const resolveDotColor = (color, status) => {
  if (color) return isPresetColor(color) ? PRESET_COLORS[color] : color
  if (status) return STATUS_DOT_COLOR[status] || STATUS_DOT_COLOR.default
  return 'var(--error-color)'
}

const Ribbon = ({ text, color, placement = 'end', className, children, ...rest }) => (
  <div className={cn('ui-ribbon-wrapper relative', className)} {...rest}>
    {children}
    <div
      className="ui-ribbon absolute top-2 px-2 py-0.5 text-xs font-medium text-white"
      style={{
        backgroundColor: color ? (isPresetColor(color) ? PRESET_COLORS[color] : color) : 'var(--primary-color)',
        [placement === 'end' ? 'right' : 'left']: 0,
        borderRadius: placement === 'end' ? '4px 0 0 4px' : '0 4px 4px 0',
      }}
    >
      {text}
    </div>
  </div>
)

/**
 * antd Badge. Covers the standalone status/dot form, the wrapping counter form,
 * `overflowCount`, `showZero` and `offset`.
 * forwardRef so Dropdown / Tooltip can attach an anchor ref.
 */
const Badge = forwardRef(({
  count,
  showZero = false,
  overflowCount = 99,
  dot = false,
  status,
  text,
  color,
  size = 'default',
  offset,
  title,
  className,
  style,
  children,
  ...rest
}, ref) => {
  const numericCount = typeof count === 'number' ? count : null
  const displayCount =
    numericCount != null && numericCount > overflowCount ? `${overflowCount}+` : count

  const isHidden =
    !dot && (count == null || (numericCount === 0 && !showZero)) && !status && !text

  // Standalone status form: <Badge status="success" text="Actif" />
  if (!children && (status || (text && !count))) {
    return (
      <span
        ref={ref}
        className={cn('ui-badge ui-badge-status inline-flex items-center gap-1.5', className)}
        style={style}
        {...rest}
      >
        <span
          className="ui-badge-status-dot inline-block h-1.5 w-1.5 rounded-full"
          style={{ backgroundColor: resolveDotColor(color, status) }}
        />
        {text ? <span className="ui-badge-status-text text-sm">{text}</span> : null}
      </span>
    )
  }

  const indicator = isHidden ? null : dot ? (
    <span
      className="ui-badge-dot absolute block h-1.5 w-1.5 -translate-y-1/2 translate-x-1/2 rounded-full ring-1 ring-white"
      style={{
        backgroundColor: resolveDotColor(color, status),
        top: offset?.[1] ?? 0,
        right: -(offset?.[0] ?? 0),
      }}
    />
  ) : (
    <span
      className={cn(
        'ui-badge-count ui-numeric absolute inline-flex -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full px-1.5 font-medium text-white ring-1 ring-white',
        size === 'small' ? 'h-4 min-w-[16px] text-[10px]' : 'h-5 min-w-[20px] text-[11px]'
      )}
      style={{
        backgroundColor: resolveDotColor(color, status),
        top: offset?.[1] ?? 0,
        right: -(offset?.[0] ?? 0),
      }}
      title={title ?? (numericCount != null ? String(numericCount) : undefined)}
    >
      {displayCount}
    </span>
  )

  if (!children) {
    return (
      <span ref={ref} className={cn('ui-badge relative inline-block', className)} style={style} {...rest}>
        <span className="invisible">0</span>
        {indicator}
      </span>
    )
  }

  return (
    <span ref={ref} className={cn('ui-badge relative inline-block', className)} style={style} {...rest}>
      {children}
      {indicator}
    </span>
  )
})

Badge.displayName = 'Badge'
Badge.Ribbon = Ribbon

export default Badge
